/**
 * Better Auth React client for OttoTester — Phase 2 of ottotester#65.
 *
 * Maps 1:1 to the backend handler mounted at /api/auth/*splat in
 * apps/server/src/index.ts (Phase 1, #135). The client emits typed
 * methods for sign-in / sign-up / sign-out / session-fetch.
 *
 * The backend Organization plugin endpoints exist now (Phase 1) but
 * the matching `organizationClient()` plugin lift is deferred to
 * Phase 5 when workspace switcher UI actually consumes it — adding
 * it earlier is dead plumbing.
 *
 * No React provider is needed — Better Auth's hooks (`useSession`,
 * etc.) work directly without wrapping the tree, unlike Cognito- or
 * Auth0-style clients. Phase 2 ships only the wiring; Phase 3 adds
 * the UI screens that consume these hooks.
 *
 * baseURL: the server origin (NOT /api/auth). Better Auth's client
 * appends `/api/auth/...` automatically. Defaults to same-origin so
 * any deployment works without env config; VITE_API_URL is an
 * override for unusual setups.
 */
import { createAuthClient } from 'better-auth/react'
import { apiKeyClient } from '@better-auth/api-key/client'
import { ssoClient } from '@better-auth/sso/client'
import { oauthProviderClient } from '@better-auth/oauth-provider/client'

const API_BASE = import.meta.env.VITE_API_URL || '/api'
// When API_BASE is relative ('/api'), the server is the page's origin;
// otherwise strip the trailing /api off the absolute URL. Better Auth's
// client appends /api/auth/* itself, so we hand it the server root.
const SERVER_BASE = API_BASE.startsWith('/')
  ? window.location.origin
  : API_BASE.replace(/\/api\/?$/, '')

export const authClient = createAuthClient({
  baseURL: SERVER_BASE,
  plugins: [
    // ottotester#427 — exposes authClient.apiKey.{create,list,delete,update}
    // for the Account → API tokens section. The plugin emits a typed
    // client surface around the backend's /api/auth/api-key/* endpoints.
    apiKeyClient(),
    // #1792 — per-customer single sign-on. Gives the sign-in screen
    // `authClient.signIn.sso(...)` and the org's Single sign-on tab
    // the register / list / delete calls. The connection endpoints are
    // authorised by org role in the plugin and gated on the org's plan
    // by `ssoEntitlementGate` on the server.
    ssoClient({ domainVerification: { enabled: true } }),
    // #2488 — when this install is the OAuth provider for the account service,
    // a customer whose session lapsed can be bounced here to sign in for a
    // pending authorization. This plugin attaches that pending authorization
    // (the signed page query) to the sign-in request when the page carries
    // one, so the server can resume the handoff the moment sign-in succeeds.
    // A no-op for an ordinary sign-in, where the page carries no such query.
    oauthProviderClient(),
  ],
})

// Re-export the most commonly used hooks/methods for ergonomic imports.
// Phase 3+ UI will pull these directly via `import { useSession } from
// '@/auth/authClient'` rather than reaching into the client object.
export const {
  useSession,
  signIn,
  signOut,
  signUp,
} = authClient
